import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Check } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { setJsonLd, setPageSeo } from '../utils/seo';
import './ShopPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLLECTIONS = [
  {
    id: 'legacy-thread',
    name: 'A Thread',
    aud: 149,
    usd: 99,
    letters: 3,
    desc: "Three beautifully crafted keepsake letters capturing the memories that matter most. The perfect place to begin a story that lasts.",
    cta: 'Begin with A Thread',
  },
  {
    id: 'legacy-woven',
    name: 'Woven',
    aud: 249,
    usd: 169,
    letters: 6,
    desc: "Six letters, each one a piece of a life — a memory preservation collection that builds into something your family will treasure for generations.",
    badge: 'Most Loved',
    cta: 'Begin Woven',
  },
  {
    id: 'legacy-tapestry',
    name: 'Tapestry',
    aud: 429,
    usd: 289,
    letters: 12,
    desc: "Twelve letters. Twelve memories. A complete collection — for a life that deserves to be fully told.",
    cta: 'Begin Tapestry',
  },
];

const shopFaqs = [
  { q: "What happens after I purchase?", a: "You will receive a welcome email and your loved one will receive a personalised note explaining how Binding Thread works and providing them with our free call number." },
  { q: "When will my keepsake tin arrive?", a: "Your Keepsake tin will arrive when your loved one shares their first memory and we turn this into a keepsake letter. We will then send both to you in the post." },
  { q: "How long does my collection last?", a: "There\u2019s no schedule to keep. Each collection includes 3, 6 or 12 letters. Letters arrive as the calls come in — at your loved one\u2019s pace, in their own time. No deadlines, no pressure." },
  { q: "What if my loved one doesn\u2019t call?", a: "No worries \u2014 there is no time limit on your collection. Your letters wait for them. We send gentle reminders if it feels right, and we\u2019re always here if you\u2019d like to talk." },
  { q: "Is shipping included?", a: "Yes. Every collection includes the keepsake tin and shipping on all letters. The price you see is the price you pay." },
];

function ShopFaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`shop-faq-item ${open ? 'open' : ''}`}>
      <button className="shop-faq-q" onClick={() => setOpen(!open)} data-testid="shop-faq-q">
        <span>{q}</span>
        <ChevronDown size={18} className={`faq-icon ${open ? 'rotated' : ''}`} />
      </button>
      {open && <div className="shop-faq-a"><p>{a}</p></div>}
    </div>
  );
}

function ShopPage() {
  const { currency } = useCurrency();
  const [loadingTier, setLoadingTier] = useState(null);

  useEffect(() => {
    setPageSeo({
      title: 'Keepsake Letter Collections | Binding Thread',
      description: 'Choose your Binding Thread collection — 3, 6 or 12 beautifully crafted keepsake letters capturing your loved one\u2019s memories. Keepsake tin and shipping included in every package. From $149.',
      canonical: 'https://www.bindingthread.com.au/shop',
      ogImage: 'https://www.bindingthread.com.au/logo-final.png',
    });
    setJsonLd('ld-shop', {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Binding Thread Keepsake Letter Collections",
      "description": "Keepsake letter collections (3, 6 or 12 letters) capturing your loved one\u2019s memories. Keepsake tin and shipping included.",
      "brand": { "@type": "Brand", "name": "Binding Thread" },
      "offers": [
        { "@type": "Offer", "name": "A Thread", "price": "149", "priceCurrency": "AUD" },
        { "@type": "Offer", "name": "Woven", "price": "249", "priceCurrency": "AUD" },
        { "@type": "Offer", "name": "Tapestry", "price": "429", "priceCurrency": "AUD" },
      ],
    });
  }, []);

  const formatPrice = (c) => {
    if (currency === 'USD') return { amount: `$${c.usd}`, code: 'USD' };
    return { amount: `$${c.aud}`, code: 'AUD' };
  };

  const handlePurchase = async (collectionId) => {
    if (loadingTier) return;
    setLoadingTier(collectionId);
    try {
      const checkoutCurrency = (currency || 'AUD').toLowerCase();
      const response = await axios.post(`${API_URL}/api/checkout/create-session`, {
        product_id: collectionId,
        origin_url: window.location.origin,
        add_keepsake_tin: false,
        currency: checkoutCurrency,
        shipping_option: null,
      });
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setLoadingTier(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoadingTier(null);
    }
  };

  return (
    <div className="shop-page" data-testid="shop-page">
      <section className="shop-hero">
        <div className="container">
          <p className="shop-eyebrow">The Collections</p>
          <h1>Choose your collection.</h1>
          <p className="shop-hero-sub">
            Three carefully crafted keepsake letter collections. Keepsake tin and shipping included in every package.
          </p>
        </div>
      </section>

      <section className="collections-section">
        <div className="container">
          <div className="collections-grid">
            {COLLECTIONS.map((c) => {
              const { amount, code } = formatPrice(c);
              return (
                <div
                  key={c.id}
                  className={`collection-card ${c.badge ? 'is-featured' : ''}`}
                  data-testid={`collection-${c.id}`}
                >
                  {c.badge && <span className="collection-badge">{c.badge}</span>}
                  <img src="/legacy/keepsake-tin.png" alt="Binding Thread keepsake tin" className="collection-tin" loading="lazy" />
                  <h2 className="collection-name">{c.name}</h2>
                  <div className="collection-price" data-testid={`collection-${c.id}-price`}>
                    <span className="collection-price-amount">{amount}</span>
                    <span className="collection-price-currency">{code}</span>
                  </div>
                  <p className="collection-desc">{c.desc}</p>
                  <ul className="collection-includes">
                    <li><Check size={14} /> {c.letters} keepsake letters</li>
                    <li><Check size={14} /> Binding Thread keepsake tin</li>
                    <li><Check size={14} /> Shipping included</li>
                    <li><Check size={14} /> Free toll-free call number, day or night</li>
                  </ul>
                  <button
                    type="button"
                    className="btn btn-primary collection-cta"
                    onClick={() => handlePurchase(c.id)}
                    disabled={loadingTier !== null}
                    data-testid={`collection-${c.id}-cta`}
                  >
                    {loadingTier === c.id ? 'Loading…' : c.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="shop-faq-section">
        <div className="container">
          <h2 className="shop-faq-heading">Common Questions</h2>
          <div className="shop-faq-list">
            {shopFaqs.map((f, i) => (
              <ShopFaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>
          <p className="shop-faq-foot">
            For families facing terminal illness, you may prefer to read our{' '}
            <Link to="/legacy-collection">Legacy Collection</Link> page — same collections, written with extra care for that specific moment.
          </p>
        </div>
      </section>
    </div>
  );
}

export default ShopPage;
